/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaJobQuotes.ts
 * FILE_ID: JOBHUB-QA-JOB-QUOTES-HOOK-001
 * PURPOSE: Load the QA quote list for one job, including every status.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { JobHubHttpClient } from '../../jobHubDataAdapter'
import type { QuoteRow } from '../../jobDetailTypes'

export function qaJobQuotesKey(jobId: number) {
  return ['qa-job-quotes', jobId] as const
}

export function useQaJobQuotes(jobId: number, client: JobHubHttpClient) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: qaJobQuotesKey(jobId),
    enabled: Number.isFinite(jobId) && jobId > 0,
    queryFn: async () => {
      const response = await client.get(`/api/qa/jobs/${jobId}/quotes/`)
      const payload = response.data
      if (!Array.isArray(payload)) throw new Error('Could not load quotes.')
      return payload as QuoteRow[]
    },
  })
  return {
    quotes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => queryClient.invalidateQueries({ queryKey: qaJobQuotesKey(jobId) }),
  }
}

export async function hideQaQuote(client: JobHubHttpClient, _jobId: number, sheetId: number) {
  await client.patch(`/api/v1/quote-sheets/${sheetId}/qa-visibility/`, { hidden_from_qa: true })
}
